// Smart Daara - My Certificates Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Award, Download, Eye, Calendar, BookOpen } from 'lucide-react';
import Certificate from '../../components/shared/Certificate';

// API call
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyCertificates() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/certificates/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCertificates(data || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-certificates">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          {language === 'fr' ? 'Mes Certificats' : 'My Certificates'}
        </h1>
        <p className="text-slate-500">
          {language === 'fr' 
            ? 'Vos réussites et accomplissements'
            : 'Your achievements and accomplishments'}
        </p>
      </div>

      {/* Selected Certificate View */}
      {selectedCert && (
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCert(null)}
            className="mb-4"
          >
            ← {language === 'fr' ? 'Retour à la liste' : 'Back to list'}
          </Button>
          <Certificate certificate={selectedCert} />
        </div>
      )}

      {/* Certificates Grid */}
      {!selectedCert && (
        <>
          {certificates.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {certificates.map((cert) => (
                <Card 
                  key={cert.id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedCert(cert)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Award className="w-7 h-7 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {cert.course_title_fr || cert.course_title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(cert.completion_date)}
                        </div>
                        <p className="text-xs text-slate-400 mt-2 font-mono">
                          {cert.certificate_number}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCert(cert);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {language === 'fr' ? 'Voir' : 'View'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <Award className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">
                  {language === 'fr' 
                    ? 'Aucun certificat pour le moment'
                    : 'No certificates yet'}
                </h3>
                <p className="text-slate-500 mb-6">
                  {language === 'fr'
                    ? 'Terminez un cours pour obtenir votre premier certificat!'
                    : 'Complete a course to earn your first certificate!'}
                </p>
                <Button onClick={() => navigate('/student/catalog')}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Voir les cours' : 'Browse courses'}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
